import { useCallback, useEffect, useState } from 'react';
import { fetchSettingDefaults, saveTerrainTheatre, type SettingDefaults } from '../api/setting';

/**
 * Who the platform proposes to fight a night, for the two steps that pick a
 * faction: from the era chosen at step 1 and from where the terrain is. Null
 * until it answers, and when it cannot (a modern night proposes nothing and
 * answers so; a failure is nothing to propose either).
 *
 * `placeTerrain` is the answer to "where is this terrain?", which the enemy
 * step asks once when the era is one in which the place decides and nobody has
 * said: it is remembered for every later night, and the proposal is asked again.
 */
export function useSettingDefaults(
  serverId: number | undefined,
  world: string | undefined,
  era: string | undefined,
): { defaults: SettingDefaults | null; placeTerrain: (theatre: string) => void } {
  const [defaults, setDefaults] = useState<SettingDefaults | null>(null);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    if (!serverId || !world) return;
    let live = true;
    fetchSettingDefaults(serverId, world, era)
      .then((d) => {
        if (live) setDefaults(d);
      })
      .catch(() => {
        if (live) setDefaults(null);
      });
    return () => {
      live = false;
    };
  }, [serverId, world, era, asked]);

  const placeTerrain = useCallback(
    (theatre: string) => {
      if (!world) return;
      saveTerrainTheatre(world, theatre)
        .then(() => setAsked((n) => n + 1))
        .catch(() => {});
    },
    [world],
  );

  return { defaults, placeTerrain };
}
